import { environment } from "@environments/environment";
import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { AuthService } from "@auth0/auth0-angular";
import { catchError, concatMap, filter, map, of, switchMap, take, throwError } from "rxjs";
import { API_CONST } from "../constants/api.constants";

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const jwtHelper = new JwtHelperService();

  if (!req.url.includes(environment.apiUrl)) {
    return next(req);
  }

  if (req.url.includes(API_CONST.USER.BUSINESS_SIGNUP)) {
    return next(req);
  }

  return auth.isLoading$.pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() =>
      auth.getAccessTokenSilently({ detailedResponse: true, cacheMode: "on" }).pipe(
        catchError((error) => {
          if (
            error.error === "login_required" ||
            error.error === "consent_required" ||
            error.message?.includes("Missing Refresh Token") ||
            error.error_description?.includes("Missing Refresh Token")
          ) {
            auth.loginWithRedirect({ appState: { target: router.url } });
            return throwError(() => new Error("Authentication required"));
          }
          return auth.getAccessTokenSilently({ detailedResponse: true, cacheMode: "off" }).pipe(
            catchError((retryError) => {
              auth.loginWithRedirect({ appState: { target: router.url } });
              return throwError(() => retryError);
            })
          );
        })
      )
    ),
    switchMap((tokenResponse) => {
      if (jwtHelper.isTokenExpired(tokenResponse.access_token)) {
        return auth.getAccessTokenSilently({ detailedResponse: true, cacheMode: "off" }).pipe(
          catchError((error) => {
            auth.loginWithRedirect({ appState: { target: router.url } });
            return throwError(() => error);
          })
        );
      }
      return of(tokenResponse);
    }),
    concatMap((tokenResponse) => {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      });
      return next(req).pipe(
        map((event: HttpEvent<unknown>) => {
          if (event instanceof HttpResponse) {
            return event.clone({
              body: (event.body as { data?: unknown })?.data,
            });
          }
          return event;
        }),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 401) {
              auth.loginWithRedirect({ appState: { target: router.url } });
              return throwError(() => new Error("Unauthorized"));
            }
          }
          return throwError(() => error);
        })
      );
    })
  );
};
